"use client";

import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, BatteryCharging, ChevronRight, Menu, Sun, TrendingUp, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { BrandGlobe } from "@/components/landing/BrandGlobe";
import { PeakChart } from "@/components/landing/PeakChart";
import { Timeline } from "@/components/landing/Timeline";
import {
  BrandList,
  CookieSettingsLink,
  FaqAccordion,
  Flag,
  LanguageSwitcher,
  MockupShowcase,
  PersonaToggle,
  ScrollRevealText,
  VideoBentoCard,
  renderAccented,
  type FaqItem,
  type MockupPanel,
} from "@/components/landing/sections";
import { WelcomeDialog, type Persona } from "@/components/landing/WelcomeDialog";
import { APP_LOGIN_URL, APP_TRIAL_URL } from "@/lib/links";
import { localePath, type Locale } from "@/lib/i18n";
import { homeFaqs } from "@/lib/seo";
import { capture } from "@/lib/analytics";

const ONBOARDING_KEY = "samvolt:onboarding";

// All page copy, keyed by locale. Headings use *asterisks* for the serif
// italic accent and \n for line breaks (rendered via renderAccented).
const COPY = {
  sv: {
    nav: { app: "App", compatibility: "Kompatibilitet", features: "Funktioner", faq: "FAQ" },
    closeMenu: "Stäng meny",
    openMenu: "Öppna meny",
    help: "Hjälp",
    myAccount: "Mitt konto",
    trialCta: "Testa utan kostnad",
    hero: {
      h1: "Låt din energi *blomstra*\nför *små* och *stora* anläggningar.",
      sub: "AI-styrning för solceller, växelriktare och batterier – kompatibel med bland annat Solinteg och Sunwoda. För privatpersoner, installatörer och säljorganisationer.",
      bankId: "Logga in med BankID och registrera din anläggning på några minuter.",
      partners: "Finns hos bland annat:",
    },
    scrollReveal:
      "Energi ska inte bara produceras – den ska förstås. Samvolt Intelligence använder AI för att analysera, förutse och optimera energiflöden i realtid, så att varje batteri, växelriktare och kilowattimme arbetar smartare – dygnet runt.",
    mockupPanels: [
      {
        heading: "Hela *hemmet* i en vy",
        body: "Sol, batteri, förbrukning och mycket mer – samlat i en tydlig översikt. Få full kontroll över din energi på några sekunder.",
      },
      {
        heading: "Automation som *aldrig* sover",
        body: "Intelligence förutser, planerar och finjusterar driften i realtid – utifrån sol, förbrukning, batteri, elpris och effekttoppar. Helt automatiskt.",
      },
      {
        heading: "Se vart varje *krona* går",
        body: "Följ produktion, förbrukning och försäljning – och exakt hur mycket du sparar, i realtid.",
      },
      {
        heading: "Fokusera på det *du* vill",
        body: "Anpassa din dashboard med widgets och se det som betyder mest – direkt.",
      },
    ],
    inverter: {
      badge: "Tillgängligt nu",
      heading: "AI-styrning för din *växelriktare* och ditt *batteri*",
      body: "Samvolt Intelligence styr hela anläggningen med hänsyn till solprognos, förbrukning, batteri, elpris, effekttoppar och oförutsedda laster i huset.",
      note: "Tillgängligt för Solinteg och Sunwoda.",
 
    },
    pilotBadge: "Pilottestas · Kommer snart",
    car: {
      heading: "Ansluten *direkt* till din elbil",
      body: "Samvolt Intelligence kopplas direkt till bilen och läser av batterinivå och räckvidd i realtid – så att laddningen alltid optimeras efter din faktiska körning.",
      note: "Stöd för de flesta elbilsmärken.",
      imgAlt: "Elbil ansluten till Intelligence",
    },
    charging: {
      heading: "Ladda bilen när det är *smartast*",
      body: "Koppla Samvolt Intelligence till din elbilsladdare. AI:n optimerar laddningen utifrån solproduktion, batteri, elpris och dina körbehov – helt automatiskt.",
      note: "Fungerar med dina befintliga laddare.",
      imgAlt: "Närbild på elbil som laddar",
    },
    heating: {
      heading: "Värm *hemmet* när det är smartast",
      body: "Samvolt Intelligence gör värmepumpen till en del av hela energisystemet och optimerar driften utifrån väder, solproduktion, batteri och elpris.",
      note: "Fungerar med din befintliga värmepump.",
      imgAlt: "Värmepump styrd av Intelligence",
    },
    bento: {
      solar: {
        title: "Solproduktion",
        subtitle: "Maximerad egenanvändning",
        description:
          "Din solel används där den skapar mest värde – i hemmet eller lagrad i batteriet, i stället för att exporteras i onödan.",
      },
      battery: {
        title: "Batterilager",
        subtitle: "Ladda billigt, använd dyrt",
        description:
          "Batteriet optimeras dygnet runt. Det laddas när energin är billig eller solen producerar och används när det ger som störst nytta.",
      },
      operation: {
        title: "Intelligent drift",
        subtitle: "Hela anläggningen i realtid",
        description:
          "Samvolt Intelligence optimerar anläggningen i realtid utifrån solprognos, förbrukning, batteri, elpris och effekttoppar.",
      },
      peaks: {
        title: "Effekttoppar",
        body: "Har du effekttariff? Då kapar vi dina dyraste effekttoppar automatiskt – och sänker nätavgiften.",
      },
      sweden: {
        badge: "Sverige",
        title: "Utvecklad i Sverige",
        body: "Svenskutvecklad med svensk serverdrift – byggd för hög säkerhet, dataskydd och driftsäkerhet.",
      },
    },
    faq: { label: "Vanliga frågor", heading: "Allt du behöver *veta*" },
    cta: {
      heading: "Låt *energin* arbeta smartare",
      body: "Logga in med BankID och registrera din anläggning på några minuter – sedan förutser och styr Samvolt Intelligence energiflödena automatiskt, dygnet runt.",
      login: "Logga in",
    },
    footer: {
      tagline: "Få ut mer av din energi\u00A0– *helt automatiskt*",
      developedInSweden: "Utvecklad i Sverige",
      product: "Produkt",
      forInstallers: "För installatörer & sälj",
      aiControl: "AI-styrning",
      about: "Om oss",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alla rättigheter förbehållna.",
      privacy: "Integritetspolicy",
      terms: "Användarvillkor",
    },
  },
  en: {
    nav: { app: "App", compatibility: "Compatibility", features: "Features", faq: "FAQ" },
    closeMenu: "Close menu",
    openMenu: "Open menu",
    help: "Help",
    myAccount: "My account",
    trialCta: "Try for free",
    hero: {
      h1: "Let your energy *flourish*\nfor *small* and *large* installations.",
      sub: "AI control for solar panels, inverters and batteries – compatible with Solinteg and Sunwoda, among others. For homeowners, installers and sales organizations.",
      bankId: "Log in with BankID and register your installation in just a few minutes.",
      partners: "Available through partners like:",
    },
    scrollReveal:
      "Energy shouldn't just be produced – it should be understood. Samvolt Intelligence uses AI to analyze, predict and optimize energy flows in real time, so that every battery, inverter and kilowatt-hour works smarter – around the clock.",
    mockupPanels: [
      {
        heading: "Your whole *home* in one view",
        body: "Solar, battery, consumption and much more – gathered in a clear overview. Get full control of your energy in seconds.",
      },
      {
        heading: "Automation that *never* sleeps",
        body: "Intelligence predicts, plans and fine-tunes operation in real time – based on solar, consumption, battery, electricity prices and power peaks. Fully automatic.",
      },
      {
        heading: "See where every *krona* goes",
        body: "Track production, consumption and sales – and exactly how much you save, in real time.",
      },
      {
        heading: "Focus on what *you* want",
        body: "Customize your dashboard with widgets and see what matters most – instantly.",
      },
    ],
    inverter: {
      badge: "Available now",
      heading: "AI control for your *inverter* and your *battery*",
      body: "Connect Solinteg or Sunwoda and let Intelligence control the installation as a whole – solar forecast, consumption, battery, electricity prices and power peaks in the same flow. Built for homeowners, installers and sales teams.",
      note: "Available for Solinteg and Sunwoda.",
    },
    pilotBadge: "In pilot testing · Coming soon",
    car: {
      heading: "Connected *directly* to your EV",
      body: "Samvolt Intelligence connects directly to your car and reads battery level and range in real time – so charging is always optimized around your actual driving.",
      note: "Supports most EV brands.",
      imgAlt: "Electric car connected to Intelligence",
    },
    charging: {
      heading: "Charge your car when it's *smartest*",
      body: "Connect Samvolt Intelligence to your EV charger. The AI optimizes charging based on solar production, battery, electricity prices and your driving needs – fully automatically.",
      note: "Works with your existing chargers.",
      imgAlt: "Close-up of an electric car charging",
    },
    heating: {
      heading: "Heat your *home* when it's smartest",
      body: "Samvolt Intelligence makes the heat pump part of your entire energy system and optimizes operation based on weather, solar production, battery and electricity prices.",
      note: "Works with your existing heat pump.",
      imgAlt: "Heat pump controlled by Intelligence",
    },
    bento: {
      solar: {
        title: "Solar production",
        subtitle: "Maximized self-consumption",
        description:
          "Your solar energy is used where it creates the most value – in the home or stored in the battery, instead of being exported unnecessarily.",
      },
      battery: {
        title: "Battery storage",
        subtitle: "Charge cheap, use when expensive",
        description:
          "The battery is optimized around the clock. It charges when energy is cheap or the sun is producing and is used when it delivers the most value.",
      },
      operation: {
        title: "Intelligent operation",
        subtitle: "The whole installation in real time",
        description:
          "Samvolt Intelligence optimizes the installation in real time based on solar forecasts, consumption, battery, electricity prices and power peaks.",
      },
      peaks: {
        title: "Power peaks",
        body: "On a power tariff? We automatically shave your most expensive power peaks – and lower your grid fees.",
      },
      sweden: {
        badge: "Sweden",
        title: "Developed in Sweden",
        body: "Swedish-built with Swedish server operations – engineered for high security, data protection and reliability.",
      },
    },
    faq: { label: "Frequently asked questions", heading: "Everything you need to *know*" },
    cta: {
      heading: "Let your *energy* work smarter",
      body: "Log in with BankID and register your installation in a few minutes – then Samvolt Intelligence predicts and controls your energy flows automatically, around the clock.",
      login: "Log in",
    },
    footer: {
      tagline: "Get more out of your energy\u00A0– *fully automatic*",
      developedInSweden: "Developed in Sweden",
      product: "Product",
      forInstallers: "For installers & sales",
      // The Swedish column mixes keyword pages with company links; in English
      // only the company links remain, so the header says "Company".
      aiControl: "Company",
      about: "About us",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. All rights reserved.",
      privacy: "Privacy policy",
      terms: "Terms of use",
    },
  },
  no: {
    nav: { app: "App", compatibility: "Kompatibilitet", features: "Funksjoner", faq: "FAQ" },
    closeMenu: "Lukk meny",
    openMenu: "Åpne meny",
    help: "Hjelp",
    myAccount: "Min konto",
    trialCta: "Prøv gratis",
    hero: {
      h1: "La energien din *blomstre*\nfor *små* og *store* anlegg.",
      sub: "AI-styring for solcellepaneler, vekselrettere og batterier – kompatibel med blant annet Solinteg og Sunwoda. For privatpersoner, installatører og salgsorganisasjoner.",
      bankId: "Logg inn med BankID og registrer anlegget ditt på bare noen få minutter.",
      partners: "Tilgjengelig hos partnere som:",
    },
    scrollReveal:
      "Energi skal ikke bare produseres – den skal forstås. Samvolt Intelligence bruker AI til å analysere, forutse og optimalisere energiflyt i sanntid, slik at hvert batteri, hver vekselretter og hver kilowattime jobber smartere – døgnet rundt.",
    mockupPanels: [
      {
        heading: "Hele *hjemmet* i én oversikt",
        body: "Sol, batteri, forbruk og mye mer – samlet i én tydelig oversikt. Få full kontroll over energien din på sekunder.",
      },
      {
        heading: "Automatisering som *aldri* sover",
        body: "Intelligence forutser, planlegger og finjusterer driften i sanntid – basert på sol, forbruk, batteri, strømpris og effekttopper. Helt automatisk.",
      },
      {
        heading: "Se hvor hver *krone* går",
        body: "Følg produksjon, forbruk og salg – og nøyaktig hvor mye du sparer, i sanntid.",
      },
      {
        heading: "Fokuser på det *du* vil",
        body: "Tilpass dashbordet ditt med widgeter og se det som betyr mest – umiddelbart.",
      },
    ],
    inverter: {
      badge: "Tilgjengelig nå",
      heading: "AI-styring for *vekselretteren* og *batteriet* ditt",
      body: "Koble til Solinteg eller Sunwoda og la Intelligence styre hele anlegget som en helhet – solprognose, forbruk, batteri, strømpriser og effekttopper i samme flyt. Bygget for privatpersoner, installatører og salgsteam.",
      note: "Tilgjengelig for Solinteg og Sunwoda.",
    },
    pilotBadge: "Under pilottesting · Kommer snart",
    car: {
      heading: "Koblet *direkte* til elbilen din",
      body: "Samvolt Intelligence kobles direkte til bilen din og leser av batterinivå og rekkevidde i sanntid – slik at ladingen alltid optimaliseres etter din faktiske kjøring.",
      note: "Støtter de fleste elbilmerker.",
      imgAlt: "Elbil koblet til Intelligence",
    },
    charging: {
      heading: "Lad bilen når det er *smartest*",
      body: "Koble Samvolt Intelligence til elbilladeren din. AI-en optimaliserer ladingen basert på solproduksjon, batteri, strømpriser og kjørebehovene dine – helt automatisk.",
      note: "Fungerer med laderne du allerede har.",
      imgAlt: "Nærbilde av en elbil som lades",
    },
    heating: {
      heading: "Varm opp *hjemmet* når det er smartest",
      body: "Samvolt Intelligence gjør varmepumpen til en del av hele energisystemet ditt og optimaliserer driften basert på vær, solproduksjon, batteri og strømpriser.",
      note: "Fungerer med varmepumpen du allerede har.",
      imgAlt: "Varmepumpe styrt av Intelligence",
    },
    bento: {
      solar: {
        title: "Solproduksjon",
        subtitle: "Maksimert egenforbruk",
        description:
          "Solenergien din brukes der den skaper mest verdi – i hjemmet eller lagret i batteriet, i stedet for å eksporteres unødvendig.",
      },
      battery: {
        title: "Batterilagring",
        subtitle: "Lad billig, bruk når det er dyrt",
        description:
          "Batteriet optimaliseres døgnet rundt. Det lades når energien er billig eller solen produserer, og brukes når det gir størst verdi.",
      },
      operation: {
        title: "Intelligent drift",
        subtitle: "Hele anlegget i sanntid",
        description:
          "Samvolt Intelligence optimaliserer anlegget i sanntid basert på solprognoser, forbruk, batteri, strømpriser og effekttopper.",
      },
      peaks: {
        title: "Effekttopper",
        body: "Har du effekttariff? Vi kapper automatisk de dyreste effekttoppene dine – og senker nettleien.",
      },
      sweden: {
        badge: "Sverige",
        title: "Utviklet i Sverige",
        body: "Svenskbygget med svensk serverdrift – utviklet for høy sikkerhet, personvern og driftssikkerhet.",
      },
    },
    faq: { label: "Vanlige spørsmål", heading: "Alt du trenger å *vite*" },
    cta: {
      heading: "La *energien* jobbe smartere",
      body: "Logg inn med BankID og registrer anlegget ditt på noen få minutter – så forutser og styrer Samvolt Intelligence energiflyten automatisk, døgnet rundt.",
      login: "Logg inn",
    },
    footer: {
      tagline: "Få mer ut av energien din\u00A0– *helt automatisk*",
      developedInSweden: "Utviklet i Sverige",
      product: "Produkt",
      forInstallers: "For installatører & salg",
      aiControl: "Selskap",
      about: "Om oss",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle rettigheter forbeholdt.",
      privacy: "Personvernpolicy",
      terms: "Bruksvilkår",
    },
  },
  da: {
    nav: { app: "App", compatibility: "Kompatibilitet", features: "Funktioner", faq: "FAQ" },
    closeMenu: "Luk menu",
    openMenu: "Åbn menu",
    help: "Hjælp",
    myAccount: "Min konto",
    trialCta: "Prøv gratis",
    hero: {
      h1: "Lad din energi *blomstre*\nfor *små* og *store* anlæg.",
      sub: "AI-styring til solpaneler, invertere og batterier – kompatibel med blandt andet Solinteg og Sunwoda. Til private, installatører og salgsorganisationer.",
      bankId: "Log ind med BankID, og registrér dit anlæg på få minutter.",
      partners: "Fås hos partnere som:",
    },
    scrollReveal:
      "Energi skal ikke bare produceres – den skal forstås. Samvolt Intelligence bruger AI til at analysere, forudsige og optimere energistrømme i realtid, så hvert batteri, hver inverter og hver kilowatt-time arbejder smartere – døgnet rundt.",
    mockupPanels: [
      {
        heading: "Hele *hjemmet* i ét overblik",
        body: "Sol, batteri, forbrug og meget mere – samlet i ét tydeligt overblik. Få fuld kontrol over din energi på sekunder.",
      },
      {
        heading: "Automatisering, der *aldrig* sover",
        body: "Intelligence forudser, planlægger og finjusterer driften i realtid – baseret på sol, forbrug, batteri, elpriser og effekttoppe. Helt automatisk.",
      },
      {
        heading: "Se hvor hver *krone* går",
        body: "Følg produktion, forbrug og salg – og præcis hvor meget du sparer, i realtid.",
      },
      {
        heading: "Fokusér på det *du* vil",
        body: "Tilpas dit dashboard med widgets, og se det, der betyder mest – med det samme.",
      },
    ],
    inverter: {
      badge: "Tilgængelig nu",
      heading: "AI-styring til din *inverter* og dit *batteri*",
      body: "Tilslut Solinteg eller Sunwoda, og lad Intelligence styre hele anlægget som en helhed – solprognose, forbrug, batteri, elpriser og effekttoppe i samme flow. Bygget til private, installatører og salgsteams.",
      note: "Tilgængelig for Solinteg og Sunwoda.",
    },
    pilotBadge: "Under pilottest · Kommer snart",
    car: {
      heading: "Forbundet *direkte* til din elbil",
      body: "Samvolt Intelligence forbindes direkte til din bil og aflæser batteriniveau og rækkevidde i realtid – så opladningen altid optimeres efter din faktiske kørsel.",
      note: "Understøtter de fleste elbilmærker.",
      imgAlt: "Elbil forbundet til Intelligence",
    },
    charging: {
      heading: "Oplad bilen, når det er *smartest*",
      body: "Tilslut Samvolt Intelligence til din elbil-lader. AI'en optimerer opladningen ud fra solproduktion, batteri, elpriser og dine kørselsbehov – helt automatisk.",
      note: "Fungerer med dine eksisterende ladere.",
      imgAlt: "Nærbillede af en elbil, der oplades",
    },
    heating: {
      heading: "Varm *hjemmet* op, når det er smartest",
      body: "Samvolt Intelligence gør varmepumpen til en del af hele dit energisystem og optimerer driften ud fra vejr, solproduktion, batteri og elpriser.",
      note: "Fungerer med din eksisterende varmepumpe.",
      imgAlt: "Varmepumpe styret af Intelligence",
    },
    bento: {
      solar: {
        title: "Solproduktion",
        subtitle: "Maksimeret eget forbrug",
        description:
          "Din solenergi bruges der, hvor den skaber mest værdi – i hjemmet eller lagret i batteriet i stedet for at blive eksporteret unødvendigt.",
      },
      battery: {
        title: "Batterilager",
        subtitle: "Lad billigt op, brug når det er dyrt",
        description:
          "Batteriet optimeres døgnet rundt. Det lades op, når energien er billig, eller solen producerer, og bruges, når det giver størst værdi.",
      },
      operation: {
        title: "Intelligent drift",
        subtitle: "Hele anlægget i realtid",
        description:
          "Samvolt Intelligence optimerer anlægget i realtid ud fra solprognoser, forbrug, batteri, elpriser og effekttoppe.",
      },
      peaks: {
        title: "Effekttoppe",
        body: "Har du en effekttarif? Vi skærer automatisk dine dyreste effekttoppe – og sænker dine netafgifter.",
      },
      sweden: {
        badge: "Sverige",
        title: "Udviklet i Sverige",
        body: "Svensk-bygget med svensk serverdrift – konstrueret til høj sikkerhed, databeskyttelse og driftssikkerhed.",
      },
    },
    faq: { label: "Ofte stillede spørgsmål", heading: "Alt hvad du behøver at *vide*" },
    cta: {
      heading: "Lad din *energi* arbejde smartere",
      body: "Log ind med BankID, og registrér dit anlæg på få minutter – så forudser og styrer Samvolt Intelligence dine energistrømme automatisk, døgnet rundt.",
      login: "Log ind",
    },
    footer: {
      tagline: "Få mere ud af din energi\u00A0– *helt automatisk*",
      developedInSweden: "Udviklet i Sverige",
      product: "Produkt",
      forInstallers: "Til installatører & salg",
      aiControl: "Virksomhed",
      about: "Om os",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle rettigheder forbeholdes.",
      privacy: "Privatlivspolitik",
      terms: "Brugsvilkår",
    },
  },
  fi: {
    nav: { app: "Sovellus", compatibility: "Yhteensopivuus", features: "Ominaisuudet", faq: "UKK" },
    closeMenu: "Sulje valikko",
    openMenu: "Avaa valikko",
    help: "Ohje",
    myAccount: "Oma tili",
    trialCta: "Kokeile ilmaiseksi",
    hero: {
      h1: "Anna energiasi *kukoistaa*\n*pieniin* ja *suuriin* laitteistoihin.",
      sub: "Tekoälyohjaus aurinkopaneeleille, inverttereille ja akuille – yhteensopiva muun muassa Solintegin ja Sunwodan kanssa. Yksityishenkilöille, asentajille ja myyntiorganisaatioille.",
      bankId: "Kirjaudu sisään BankID:llä ja rekisteröi laitteistosi muutamassa minuutissa.",
      partners: "Saatavilla kumppaneilta, kuten:",
    },
    scrollReveal:
      "Energiaa ei tulisi vain tuottaa – se tulisi ymmärtää. Samvolt Intelligence hyödyntää tekoälyä analysoidakseen, ennustaakseen ja optimoidakseen energiavirtoja reaaliajassa, jotta jokainen akku, invertteri ja kilowattitunti toimii älykkäämmin – vuorokauden ympäri.",
    mockupPanels: [
      {
        heading: "Koko *koti* yhdellä näkymällä",
        body: "Aurinko, akku, kulutus ja paljon muuta – koottuna yhteen selkeään näkymään. Saat energiasi täyteen hallintaan sekunneissa.",
      },
      {
        heading: "Automaatio, joka *ei koskaan* nuku",
        body: "Intelligence ennustaa, suunnittelee ja hienosäätää toimintaa reaaliajassa – auringon, kulutuksen, akun, sähkön hinnan ja tehohuippujen perusteella. Täysin automaattisesti.",
      },
      {
        heading: "Näe minne jokainen *euro* menee",
        body: "Seuraa tuotantoa, kulutusta ja myyntiä – ja tarkalleen kuinka paljon säästät, reaaliajassa.",
      },
      {
        heading: "Keskity siihen, mitä *sinä* haluat",
        body: "Mukauta koontinäyttösi widgeteillä ja näe se, mikä on tärkeintä – heti.",
      },
    ],
    inverter: {
      badge: "Saatavilla nyt",
      heading: "Tekoälyohjaus *invertterillesi* ja *akullesi*",
      body: "Yhdistä Solinteg tai Sunwoda ja anna Intelligencen ohjata koko laitteistoa kokonaisuutena – aurinkoennuste, kulutus, akku, sähkön hinnat ja tehohuiput samassa virrassa. Rakennettu yksityishenkilöille, asentajille ja myyntitiimeille.",
      note: "Saatavilla Solintegille ja Sunwodalle.",
    },
    pilotBadge: "Pilottitestauksessa · Tulossa pian",
    car: {
      heading: "Yhdistetty *suoraan* sähköautoosi",
      body: "Samvolt Intelligence yhdistyy suoraan autoosi ja lukee akun varaustason ja toimintamatkan reaaliajassa – jotta lataus optimoidaan aina todellisen ajosi mukaan.",
      note: "Tukee useimpia sähköautomerkkejä.",
      imgAlt: "Sähköauto yhdistettynä Intelligenceen",
    },
    charging: {
      heading: "Lataa autosi silloin, kun se on *älykkäintä*",
      body: "Yhdistä Samvolt Intelligence sähköauton laturiisi. Tekoäly optimoi latauksen aurinkotuotannon, akun, sähkön hintojen ja ajotarpeidesi perusteella – täysin automaattisesti.",
      note: "Toimii nykyisten latureidesi kanssa.",
      imgAlt: "Lähikuva latautuvasta sähköautosta",
    },
    heating: {
      heading: "Lämmitä *koti* silloin, kun se on älykkäintä",
      body: "Samvolt Intelligence tekee lämpöpumpusta osan koko energiajärjestelmääsi ja optimoi toiminnan sään, aurinkotuotannon, akun ja sähkön hintojen perusteella.",
      note: "Toimii nykyisen lämpöpumppusi kanssa.",
      imgAlt: "Intelligencen ohjaama lämpöpumppu",
    },
    bento: {
      solar: {
        title: "Aurinkotuotanto",
        subtitle: "Maksimoitu oma käyttö",
        description:
          "Aurinkoenergiasi käytetään siellä, missä se luo eniten arvoa – kotona tai akkuun varastoituna sen sijaan, että se vietäisiin turhaan verkkoon.",
      },
      battery: {
        title: "Akkuvarasto",
        subtitle: "Lataa halvalla, käytä kun kallista",
        description:
          "Akku optimoidaan vuorokauden ympäri. Se latautuu, kun energia on halpaa tai aurinko tuottaa, ja sitä käytetään, kun siitä on eniten hyötyä.",
      },
      operation: {
        title: "Älykäs toiminta",
        subtitle: "Koko laitteisto reaaliajassa",
        description:
          "Samvolt Intelligence optimoi laitteiston reaaliajassa aurinkoennusteiden, kulutuksen, akun, sähkön hintojen ja tehohuippujen perusteella.",
      },
      peaks: {
        title: "Tehohuiput",
        body: "Onko sinulla tehotariffi? Leikkaamme automaattisesti kalleimmat tehohuippusi – ja alennamme verkkomaksujasi.",
      },
      sweden: {
        badge: "Ruotsi",
        title: "Kehitetty Ruotsissa",
        body: "Ruotsissa rakennettu ja ruotsalaisella palvelinkäytöllä – suunniteltu korkeaan turvallisuuteen, tietosuojaan ja käyttövarmuuteen.",
      },
    },
    faq: { label: "Usein kysytyt kysymykset", heading: "Kaikki mitä sinun tarvitsee *tietää*" },
    cta: {
      heading: "Anna *energiasi* toimia älykkäämmin",
      body: "Kirjaudu sisään BankID:llä ja rekisteröi laitteistosi muutamassa minuutissa – sitten Samvolt Intelligence ennustaa ja ohjaa energiavirtojasi automaattisesti, vuorokauden ympäri.",
      login: "Kirjaudu sisään",
    },
    footer: {
      tagline: "Saa enemmän irti energiastasi\u00A0– *täysin automaattisesti*",
      developedInSweden: "Kehitetty Ruotsissa",
      product: "Tuote",
      forInstallers: "Asentajille & myynnille",
      aiControl: "Yritys",
      about: "Tietoa meistä",
      support: "Tuki",
      rights: "© 2026 Samvolt Intelligence. Kaikki oikeudet pidätetään.",
      privacy: "Tietosuojakäytäntö",
      terms: "Käyttöehdot",
    },
  },
  de: {
    nav: { app: "App", compatibility: "Kompatibilität", features: "Funktionen", faq: "FAQ" },
    closeMenu: "Menü schließen",
    openMenu: "Menü öffnen",
    help: "Hilfe",
    myAccount: "Mein Konto",
    trialCta: "Kostenlos testen",
    hero: {
      h1: "Lassen Sie Ihre Energie *aufblühen*\nfür *kleine* und *große* Anlagen.",
      sub: "KI-Steuerung für Solarmodule, Wechselrichter und Batterien – kompatibel unter anderem mit Solinteg und Sunwoda. Für Privatpersonen, Installateure und Vertriebsorganisationen.",
      bankId: "Melden Sie sich mit BankID an und registrieren Sie Ihre Anlage in nur wenigen Minuten.",
      partners: "Erhältlich bei Partnern wie:",
    },
    scrollReveal:
      "Energie sollte nicht nur erzeugt – sie sollte verstanden werden. Samvolt Intelligence nutzt KI, um Energieflüsse in Echtzeit zu analysieren, vorherzusagen und zu optimieren, sodass jede Batterie, jeder Wechselrichter und jede Kilowattstunde intelligenter arbeitet – rund um die Uhr.",
    mockupPanels: [
      {
        heading: "Ihr ganzes *Zuhause* in einer Ansicht",
        body: "Solar, Batterie, Verbrauch und vieles mehr – gebündelt in einer übersichtlichen Ansicht. Erhalten Sie in Sekunden die volle Kontrolle über Ihre Energie.",
      },
      {
        heading: "Automatisierung, die *niemals* schläft",
        body: "Intelligence prognostiziert, plant und optimiert den Betrieb in Echtzeit – auf Basis von Solar, Verbrauch, Batterie, Strompreisen und Lastspitzen. Vollautomatisch.",
      },
      {
        heading: "Sehen Sie, wohin jeder *Euro* fließt",
        body: "Verfolgen Sie Produktion, Verbrauch und Verkauf – und genau, wie viel Sie sparen, in Echtzeit.",
      },
      {
        heading: "Konzentrieren Sie sich auf das, was *Sie* wollen",
        body: "Passen Sie Ihr Dashboard mit Widgets an und sehen Sie, was am wichtigsten ist – sofort.",
      },
    ],
    inverter: {
      badge: "Jetzt verfügbar",
      heading: "KI-Steuerung für Ihren *Wechselrichter* und Ihre *Batterie*",
      body: "Verbinden Sie Solinteg oder Sunwoda und lassen Sie Intelligence die gesamte Anlage als Ganzes steuern – Solarprognose, Verbrauch, Batterie, Strompreise und Lastspitzen im selben Fluss. Entwickelt für Privatpersonen, Installateure und Vertriebsteams.",
      note: "Verfügbar für Solinteg und Sunwoda.",
    },
    pilotBadge: "Im Pilotbetrieb · Demnächst verfügbar",
    car: {
      heading: "*Direkt* mit Ihrem E-Auto verbunden",
      body: "Samvolt Intelligence verbindet sich direkt mit Ihrem Auto und liest Batteriestand und Reichweite in Echtzeit aus – damit das Laden immer auf Ihr tatsächliches Fahrverhalten abgestimmt ist.",
      note: "Unterstützt die meisten E-Auto-Marken.",
      imgAlt: "E-Auto verbunden mit Intelligence",
    },
    charging: {
      heading: "Laden Sie Ihr Auto, wenn es am *klügsten* ist",
      body: "Verbinden Sie Samvolt Intelligence mit Ihrer Wallbox. Die KI optimiert das Laden auf Basis von Solarproduktion, Batterie, Strompreisen und Ihren Fahrbedürfnissen – vollautomatisch.",
      note: "Funktioniert mit Ihren vorhandenen Ladegeräten.",
      imgAlt: "Nahaufnahme eines ladenden E-Autos",
    },
    heating: {
      heading: "Heizen Sie Ihr *Zuhause*, wenn es am klügsten ist",
      body: "Samvolt Intelligence macht die Wärmepumpe zu einem Teil Ihres gesamten Energiesystems und optimiert den Betrieb auf Basis von Wetter, Solarproduktion, Batterie und Strompreisen.",
      note: "Funktioniert mit Ihrer vorhandenen Wärmepumpe.",
      imgAlt: "Von Intelligence gesteuerte Wärmepumpe",
    },
    bento: {
      solar: {
        title: "Solarproduktion",
        subtitle: "Maximierter Eigenverbrauch",
        description:
          "Ihre Solarenergie wird dort genutzt, wo sie den größten Nutzen bringt – im Zuhause oder in der Batterie gespeichert, anstatt unnötig eingespeist zu werden.",
      },
      battery: {
        title: "Batteriespeicher",
        subtitle: "Günstig laden, teuer nutzen",
        description:
          "Die Batterie wird rund um die Uhr optimiert. Sie lädt, wenn Energie günstig ist oder die Sonne produziert, und wird genutzt, wenn sie den größten Nutzen bringt.",
      },
      operation: {
        title: "Intelligenter Betrieb",
        subtitle: "Die gesamte Anlage in Echtzeit",
        description:
          "Samvolt Intelligence optimiert die Anlage in Echtzeit auf Basis von Solarprognosen, Verbrauch, Batterie, Strompreisen und Lastspitzen.",
      },
      peaks: {
        title: "Lastspitzen",
        body: "Haben Sie einen Leistungstarif? Wir kappen automatisch Ihre teuersten Lastspitzen – und senken Ihre Netzentgelte.",
      },
      sweden: {
        badge: "Schweden",
        title: "Entwickelt in Schweden",
        body: "In Schweden entwickelt mit schwedischem Serverbetrieb – konzipiert für hohe Sicherheit, Datenschutz und Zuverlässigkeit.",
      },
    },
    faq: { label: "Häufig gestellte Fragen", heading: "Alles, was Sie *wissen* müssen" },
    cta: {
      heading: "Lassen Sie Ihre *Energie* intelligenter arbeiten",
      body: "Melden Sie sich mit BankID an und registrieren Sie Ihre Anlage in wenigen Minuten – dann prognostiziert und steuert Samvolt Intelligence Ihre Energieflüsse automatisch, rund um die Uhr.",
      login: "Anmelden",
    },
    footer: {
      tagline: "Holen Sie mehr aus Ihrer Energie heraus\u00A0– *vollautomatisch*",
      developedInSweden: "Entwickelt in Schweden",
      product: "Produkt",
      forInstallers: "Für Installateure & Vertrieb",
      aiControl: "Unternehmen",
      about: "Über uns",
      support: "Support",
      rights: "© 2026 Samvolt Intelligence. Alle Rechte vorbehalten.",
      privacy: "Datenschutzrichtlinie",
      terms: "Nutzungsbedingungen",
    },
  },
  cs: {
    nav: { app: "Aplikace", compatibility: "Kompatibilita", features: "Funkce", faq: "FAQ" },
    closeMenu: "Zavřít nabídku",
    openMenu: "Otevřít nabídku",
    help: "Nápověda",
    myAccount: "Můj účet",
    trialCta: "Vyzkoušet zdarma",
    hero: {
      h1: "Nechte svou energii *rozkvést*\npro *malé* i *velké* instalace.",
      sub: "Řízení pomocí AI pro solární panely, střídače a baterie – kompatibilní mimo jiné se Solinteg a Sunwoda. Pro domácnosti, instalatéry a prodejní organizace.",
      bankId: "Přihlaste se pomocí BankID a zaregistrujte svou instalaci během několika minut.",
      partners: "K dispozici u partnerů, jako jsou:",
    },
    scrollReveal:
      "Energie by se neměla jen vyrábět – měla by se chápat. Samvolt Intelligence využívá AI k analýze, předvídání a optimalizaci energetických toků v reálném čase, aby každá baterie, střídač a kilowatthodina pracovaly chytřeji – po celý den.",
    mockupPanels: [
      {
        heading: "Celá vaše *domácnost* v jednom pohledu",
        body: "Solár, baterie, spotřeba a mnoho dalšího – shromážděno v jednom přehledném pohledu. Získejte plnou kontrolu nad svou energií během několika sekund.",
      },
      {
        heading: "Automatizace, která *nikdy* nespí",
        body: "Intelligence předvídá, plánuje a dolaďuje provoz v reálném čase – na základě solární produkce, spotřeby, baterie, cen elektřiny a špiček výkonu. Zcela automaticky.",
      },
      {
        heading: "Uvidíte, kam jde každá *koruna*",
        body: "Sledujte výrobu, spotřebu a prodej – a přesně to, kolik ušetříte, v reálném čase.",
      },
      {
        heading: "Zaměřte se na to, co chcete *vy*",
        body: "Přizpůsobte si svůj přehled pomocí widgetů a uvidíte to, na čem záleží nejvíce – okamžitě.",
      },
    ],
    inverter: {
      badge: "Nyní k dispozici",
      heading: "Řízení pomocí AI pro váš *střídač* a vaši *baterii*",
      body: "Připojte Solinteg nebo Sunwoda a nechte Intelligence řídit celou instalaci jako celek – solární předpověď, spotřebu, baterii, ceny elektřiny a špičky výkonu v jednom toku. Vytvořeno pro domácnosti, instalatéry a prodejní týmy.",
      note: "K dispozici pro Solinteg a Sunwoda.",
    },
    pilotBadge: "V pilotním testování · Již brzy",
    car: {
      heading: "Připojeno *přímo* k vašemu elektromobilu",
      body: "Samvolt Intelligence se připojuje přímo k vašemu vozu a v reálném čase načítá stav baterie a dojezd – takže nabíjení je vždy optimalizováno podle vaší skutečné jízdy.",
      note: "Podporuje většinu značek elektromobilů.",
      imgAlt: "Elektromobil připojený k Intelligence",
    },
    charging: {
      heading: "Nabíjejte auto, když je to *nejchytřejší*",
      body: "Připojte Samvolt Intelligence ke své nabíječce elektromobilu. AI optimalizuje nabíjení na základě solární produkce, baterie, cen elektřiny a vašich potřeb jízdy – zcela automaticky.",
      note: "Funguje s vašimi stávajícími nabíječkami.",
      imgAlt: "Detailní záběr nabíjejícího se elektromobilu",
    },
    heating: {
      heading: "Vytápějte *domov*, když je to nejchytřejší",
      body: "Samvolt Intelligence činí z tepelného čerpadla součást celého vašeho energetického systému a optimalizuje provoz na základě počasí, solární produkce, baterie a cen elektřiny.",
      note: "Funguje s vaším stávajícím tepelným čerpadlem.",
      imgAlt: "Tepelné čerpadlo řízené Intelligence",
    },
    bento: {
      solar: {
        title: "Solární výroba",
        subtitle: "Maximalizovaná vlastní spotřeba",
        description:
          "Vaše solární energie se využívá tam, kde vytváří největší hodnotu – v domácnosti nebo uložená v baterii, místo aby byla zbytečně dodávána do sítě.",
      },
      battery: {
        title: "Bateriové úložiště",
        subtitle: "Nabíjejte levně, využívejte draze",
        description:
          "Baterie je optimalizována po celý den. Nabíjí se, když je energie levná nebo když produkuje slunce, a využívá se, když přináší největší hodnotu.",
      },
      operation: {
        title: "Inteligentní provoz",
        subtitle: "Celá instalace v reálném čase",
        description:
          "Samvolt Intelligence optimalizuje instalaci v reálném čase na základě solárních předpovědí, spotřeby, baterie, cen elektřiny a špiček výkonu.",
      },
      peaks: {
        title: "Špičky výkonu",
        body: "Máte tarif podle výkonu? Automaticky ořízneme vaše nejdražší špičky výkonu – a snížíme vaše poplatky za síť.",
      },
      sweden: {
        badge: "Švédsko",
        title: "Vyvinuto ve Švédsku",
        body: "Vytvořeno ve Švédsku s provozem serverů ve Švédsku – navrženo pro vysokou bezpečnost, ochranu dat a spolehlivost.",
      },
    },
    faq: { label: "Často kladené otázky", heading: "Vše, co potřebujete *vědět*" },
    cta: {
      heading: "Nechte svou *energii* pracovat chytřeji",
      body: "Přihlaste se pomocí BankID a zaregistrujte svou instalaci během několika minut – poté Samvolt Intelligence automaticky předvídá a řídí vaše energetické toky, po celý den.",
      login: "Přihlásit se",
    },
    footer: {
      tagline: "Získejte ze své energie více\u00A0– *zcela automaticky*",
      developedInSweden: "Vyvinuto ve Švédsku",
      product: "Produkt",
      forInstallers: "Pro instalatéry a prodej",
      aiControl: "Společnost",
      about: "O nás",
      support: "Podpora",
      rights: "© 2026 Samvolt Intelligence. Všechna práva vyhrazena.",
      privacy: "Zásady ochrany osobních údajů",
      terms: "Podmínky použití",
    },
  },
  zh: {
    nav: { app: "应用", compatibility: "兼容性", features: "功能", faq: "常见问题" },
    closeMenu: "关闭菜单",
    openMenu: "打开菜单",
    help: "帮助",
    myAccount: "我的账户",
    trialCta: "免费试用",
    hero: {
      h1: "让您的能源*绽放*\n适用于*小型*和*大型*系统。",
      sub: "面向太阳能板、逆变器和电池的 AI 控制——兼容 Solinteg 和 Sunwoda 等设备。适用于家庭用户、安装商和销售机构。",
      bankId: "使用 BankID 登录，几分钟内即可注册您的系统。",
      partners: "以下合作伙伴处有售：",
    },
    scrollReveal:
      "能源不应只是被生产出来——更应被理解。Samvolt Intelligence 运用 AI 实时分析、预测并优化能源流动，让每一块电池、每一台逆变器和每一度电都更聪明地运作——全天候不间断。",
    mockupPanels: [
      {
        heading: "整个*家庭*尽在一屏",
        body: "太阳能、电池、用电及更多信息——汇集于一个清晰的总览中。几秒钟内即可全面掌控您的能源。",
      },
      {
        heading: "*永不*停歇的自动化",
        body: "Intelligence 实时预测、规划并微调运行——基于太阳能、用电、电池、电价和功率峰值。全程自动。",
      },
      {
        heading: "看清每一*分钱*的去向",
        body: "实时追踪发电、用电和售电——以及您究竟节省了多少。",
      },
      {
        heading: "专注于*您*所需",
        body: "使用小组件自定义您的仪表板，即刻查看最重要的内容。",
      },
    ],
    inverter: {
      badge: "现已推出",
      heading: "为您的*逆变器*和*电池*提供 AI 控制",
      body: "连接 Solinteg 或 Sunwoda，让 Intelligence 将整个系统作为一个整体进行控制——太阳能预测、用电、电池、电价和功率峰值汇于同一流程。专为家庭用户、安装商和销售团队打造。",
      note: "支持 Solinteg 和 Sunwoda。",
    },
    pilotBadge: "试点测试中 · 即将推出",
    car: {
      heading: "*直接*连接您的电动汽车",
      body: "Samvolt Intelligence 直接连接您的车辆，实时读取电池电量和续航里程——让充电始终根据您的实际驾驶情况进行优化。",
      note: "支持大多数电动汽车品牌。",
      imgAlt: "连接到 Intelligence 的电动汽车",
    },
    charging: {
      heading: "在*最明智*的时刻为爱车充电",
      body: "将 Samvolt Intelligence 连接到您的电动汽车充电器。AI 会根据太阳能发电、电池、电价和您的驾驶需求优化充电——全程自动。",
      note: "适配您现有的充电器。",
      imgAlt: "正在充电的电动汽车特写",
    },
    heating: {
      heading: "在最明智的时刻为*家庭*供暖",
      body: "Samvolt Intelligence 让热泵成为您整个能源系统的一部分，并根据天气、太阳能发电、电池和电价优化运行。",
      note: "适配您现有的热泵。",
      imgAlt: "由 Intelligence 控制的热泵",
    },
    bento: {
      solar: {
        title: "太阳能发电",
        subtitle: "最大化自用",
        description:
          "您的太阳能会用在最有价值的地方——用于家庭或存入电池，而不是被不必要地上网出售。",
      },
      battery: {
        title: "电池储能",
        subtitle: "低价充电，高价使用",
        description:
          "电池全天候优化。在能源便宜或阳光充足时充电，在最有价值时使用。",
      },
      operation: {
        title: "智能运行",
        subtitle: "整个系统实时运行",
        description:
          "Samvolt Intelligence 根据太阳能预测、用电、电池、电价和功率峰值实时优化系统。",
      },
      peaks: {
        title: "功率峰值",
        body: "使用功率电价？我们会自动削减您最昂贵的功率峰值——并降低您的电网费用。",
      },
      sweden: {
        badge: "瑞典",
        title: "瑞典研发",
        body: "瑞典打造，采用瑞典服务器运营——为高安全性、数据保护和可靠性而设计。",
      },
    },
    faq: { label: "常见问题", heading: "您需要*了解*的一切" },
    cta: {
      heading: "让您的*能源*更聪明地运作",
      body: "使用 BankID 登录，几分钟内注册您的系统——随后 Samvolt Intelligence 将全天候自动预测并控制您的能源流动。",
      login: "登录",
    },
    footer: {
      tagline: "从您的能源中获得更多\u00A0– *全程自动*",
      developedInSweden: "瑞典研发",
      product: "产品",
      forInstallers: "面向安装商和销售",
      aiControl: "公司",
      about: "关于我们",
      support: "支持",
      rights: "© 2026 Samvolt Intelligence. 保留所有权利。",
      privacy: "隐私政策",
      terms: "使用条款",
    },
  },
} satisfies Record<Locale, unknown>;

const mockupVideos: { video: string; textSide: "left" | "right" }[] = [
  { video: "/home.webm", textSide: "left" },
  { video: "/scheme.webm", textSide: "right" },
  { video: "/savings.webm", textSide: "left" },
  { video: "/widget.webm", textSide: "right" },
];

// Supported brands, ordered most-popular-first. The first few show by default;
// the rest reveal behind a "+N fler" toggle in <BrandList>.
const inverterBrands = ["Solinteg", "Sunwoda"];

const carBrands = [
  "Tesla",
  "Volvo",
  "Volkswagen",
  "Polestar",
  "Kia",
  "Audi",
  "Mercedes",
  "Hyundai",
  "BYD",
  "Peugeot",
  "Citroën",
  "Dacia",
  "Fiat",
  "Honda",
  "Jaguar",
  "Jeep",
  "Land Rover",
  "Lexus",
  "Mazda",
  "Nissan",
  "Opel",
  "Porsche",
  "Renault",
  "Rivian",
  "Subaru",
  "Toyota",
  "XPENG",
];

const evChargers = [
  "Easee",
  "Zaptec",
  "Charge Amps",
  "Garo",
  "Wallbox",
  "go-e",
  "KEBA",
  "Heidelberg",
  "myenergi",
  "Schneider Electric",
  "Tesla",
];

const heatPumps = [
  "NIBE",
  "Bosch",
  "Daikin",
  "Mitsubishi",
  "Panasonic",
  "Toshiba",
  "Fujitsu",
  "ADAX",
  "Ecobee",
  "Mill",
  "Sensibo",
  "Tado",
  "Honeywell TCC",
  "Resideo",
  "Function",
];

export default function HomePage({ locale = "sv" }: { locale?: Locale }) {
  const t = COPY[locale];
  // Serif accent used inside dark-on-light section headings.
  const darkAccent = "font-newsreader font-normal italic text-neutral-900/60";

  const faqItems: FaqItem[] = homeFaqs(locale);

  const mockupPanels: MockupPanel[] = t.mockupPanels.map((panel, i) => ({
    ...panel,
    ...mockupVideos[i],
  }));

  // Logos for the carousel strip
  const partners = [
    { name: "Ahlsell", src: "/partners/ahlsell.svg" },
    { name: "Rexel", src: "/partners/rexel.svg" },
    { name: "KP", src: "/partners/kp.webp" },
    { name: "Horizon", src: "/partners/horizon-logo.png" },
    { name: "Energy Group", src: "/partners/energygroup-logo.webp" },
  ];

  // Primary nav links – shown inline on desktop, inside the hamburger on mobile.
  // Each points at a real in-page section via its anchor ID.
  const navLinks = [
    { label: t.nav.app, href: "#app" },
    { label: t.nav.compatibility, href: "#kompabilitet" },
    { label: t.nav.features, href: "#funktioner" },
    { label: t.nav.faq, href: "#faq" },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const router = useRouter();

  // This is the private (consumer) view. The business view lives at /foretag.
  const persona: Persona = "private";

  // Remember the persona choice, then route to the matching view.
  const choosePersona = (next: Persona) => {
    try {
      const prev = JSON.parse(localStorage.getItem(ONBOARDING_KEY) || "{}");
      localStorage.setItem(ONBOARDING_KEY, JSON.stringify({ ...prev, persona: next }));
    } catch {
      /* ignore unwritable storage */
    }
    router.push(localePath(locale, next === "business" ? "/foretag" : "/"));
  };

  // Lock background scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <main className="relative w-full bg-black font-sans text-white">
      {/* First-visit onboarding: policy consent + persona choice */}
      <WelcomeDialog locale={locale} onComplete={choosePersona} />

      {/* ============================================================
          MOBILE MENU – full-screen overlay (hamburger target)
          ============================================================ */}
      {/* Dimmed backdrop */}
      <div
        onClick={() => setMenuOpen(false)}
        className={`fixed inset-0 z-[55] bg-black/40 transition-opacity duration-300 ease-out md:hidden ${
          menuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden
      />
      {/* iOS-style frosted sheet, slides up from the bottom */}
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed inset-x-0 bottom-0 z-[60] flex max-h-[92svh] flex-col rounded-t-[28px] border-t border-white/10 bg-neutral-950/80 pb-[max(env(safe-area-inset-bottom),1.25rem)] shadow-[0_-20px_60px_-12px_rgba(0,0,0,0.6)] backdrop-blur-2xl transition-transform duration-[400ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden ${
          menuOpen ? "translate-y-0" : "pointer-events-none translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 pb-5 pt-6">
          <img
            src="/brand/intelligence-l.svg"
            alt="Samvolt Intelligence"
            className="h-7 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label={t.closeMenu}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/15 hover:text-white"
          >
            <X size={18} strokeWidth={2.25} />
          </button>
        </div>

        <div className="flex flex-col gap-6 overflow-y-auto px-5 pb-2">
          {/* Primary navigation – iOS grouped list */}
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] divide-y divide-white/10">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-between px-5 py-4 text-[1.0625rem] font-medium text-white transition-colors active:bg-white/10"
              >
                {link.label}
                <ChevronRight size={18} strokeWidth={2} className="text-white/30" />
              </a>
            ))}
          </div>

          {/* Secondary links – iOS grouped list */}
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] divide-y divide-white/10">
            <a
              href={localePath(locale, "/support")}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-5 py-4 text-[1.0625rem] text-white/85 transition-colors active:bg-white/10"
            >
              {t.help}
              <ChevronRight size={18} strokeWidth={2} className="text-white/30" />
            </a>
            <a
              href={APP_LOGIN_URL}
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-5 py-4 text-[1.0625rem] text-white/85 transition-colors active:bg-white/10"
            >
              {t.myAccount}
              <ArrowUpRight size={18} strokeWidth={2} className="text-white/30" />
            </a>
            <LanguageSwitcher locale={locale} path="/" variant="menu" />
          </div>
        </div>

        <div className="px-5 pt-5">
          <a
            href={APP_TRIAL_URL}
            onClick={() => setMenuOpen(false)}
            className="flex w-full items-center justify-center rounded-[14px] bg-white px-6 py-4 text-[1rem] font-semibold text-black transition-colors hover:bg-white/90 active:bg-white/80"
          >
            {t.trialCta}
          </a>
        </div>
      </div>

      {/* First viewport: header + hero fill the screen exactly so the
          white frame around the hero is even on every side */}
      <div className="flex h-svh flex-col">
        {/* ============================================================
          TOP UTILITY BAR – slim nav above the main header
          ============================================================ */}
        <div className="relative z-50 flex h-8 items-center justify-between px-5 md:px-7">
          <PersonaToggle active={persona} onSelect={choosePersona} locale={locale} />
          <div className="flex items-center gap-3.5 sm:gap-6">
            <a
              href={localePath(locale, "/support")}
              className="text-[0.75rem] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.help}
            </a>
            <a
              href={APP_LOGIN_URL}
              className="inline-flex items-center gap-1 text-[0.75rem] tracking-tight text-white/60 transition-colors hover:text-white"
            >
              {t.myAccount}
              <ArrowUpRight size={10} strokeWidth={2} />
            </a>
            <LanguageSwitcher locale={locale} path="/" />
          </div>
        </div>
        {/* ============================================================
          HERO – inset, rounded card with white padding around it
          ============================================================ */}
        <div className="min-h-0 flex-1 p-1.5 md:p-2 pt-0 md:pt-0">
          <section className="relative h-full min-h-[28rem] w-full overflow-hidden rounded-[16px] md:rounded-[22px]">
            {/* Background video */}
            <video className="absolute brightness-120 inset-0 h-full w-full object-cover" autoPlay muted loop playsInline preload="auto">
              <source src="/hero-videos/hero_video_av1.webm" type='video/webm; codecs="av01.0.05M.08"' />
              <source src="/hero-videos/hero_video_mp4.mp4" type="video/mp4" />
            </video>

            {/* Subtle darkening for legibility */}

            {/* Content */}
            <div className="relative z-10 flex h-full flex-col">
              {/* Main nav – inside the hero card, over the video */}
              <header className="relative flex items-center justify-between px-5 pt-5 md:px-7 md:pt-6">
                {/* Logo */}
                <a href={localePath(locale, "/")} className="inline-flex items-center">
                  <img
                    src="/brand/intelligence-l.svg"
                    alt="Samvolt Intelligence"
                    className="h-7 w-auto md:h-9"
                    style={{ filter: "brightness(0) invert(1)" }}
                  />
                </a>

                {/* Right nav – inline on desktop */}
                <nav className="hidden items-center gap-8 text-[0.875rem] md:flex">
                  {navLinks.map((link) => (
                    <a key={link.label} href={link.href} className="text-white/70 transition-colors hover:text-white">
                      {link.label}
                    </a>
                  ))}
                </nav>

                {/* Hamburger – mobile only */}
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  aria-label={t.openMenu}
                  className="-mr-1 flex items-center justify-center p-1 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)] transition-opacity hover:opacity-80 md:hidden"
                >
                  <Menu size={28} strokeWidth={2} />
                </button>
              </header>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Hero bottom row */}
              <div className="flex items-end justify-between gap-6 px-5 pb-7 md:px-7 md:pb-8">
                <div>
                  <h1 className="max-w-3xl text-balance font-display text-[2.75rem] font-normal leading-[1.02] tracking-[-0.035em] text-white md:text-[3.25rem] lg:max-w-4xl lg:text-[3.5rem] 2xl:max-w-6xl 2xl:text-[4.25rem]">
                    {renderAccented(t.hero.h1)}
                  </h1>
                  <p className="mt-4 max-w-xl text-[0.9375rem] leading-relaxed text-white/75 md:text-[1rem]">
                    {t.hero.sub}
                  </p>

                  <a
                    href={APP_TRIAL_URL}
                    onClick={() => capture("hero_trial_cta_clicked")}
                    className="group mt-8 inline-flex items-center rounded-[4px] border border-blue-500/40 bg-blue-600 px-5 py-2.5 text-[0.875rem] font-medium text-white shadow-[0_8px_24px_-8px_rgba(37,99,235,0.6)] transition-colors hover:bg-blue-500"
                  >
                    {t.trialCta}
                    <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
                      <span className="overflow-hidden">
                        <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-white/25 text-white">
                          <ArrowRight size={13} strokeWidth={2} />
                        </span>
                      </span>
                    </span>
                  </a>
                  <p className="mt-3 max-w-sm text-[0.8445rem] leading-relaxed text-white/80">
                    {t.hero.bankId}
                  </p>
                </div>
              </div>

              {/* Logo carousel strip */}
              <section className="px-5 pb-6 md:px-7 md:pb-7">
                <p className="text-[0.75rem] text-white/45">{t.hero.partners}</p>
                <div
                  className="relative mt-3 flex items-center overflow-hidden border-t border-white/10 pt-5"
                  style={{
                    WebkitMaskImage: "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                    maskImage: "linear-gradient(to right, transparent 0, black 72px, black calc(100% - 72px), transparent 100%)",
                  }}
                >
                  <div className="flex w-max animate-logo-scroll items-center">
                    {[...Array(6)]
                      .flatMap(() => partners)
                      .map((logo, index) => (
                        <div key={index} className="flex shrink-0 items-center justify-center px-7 sm:px-10">
                          <img
                            src={logo.src}
                            alt={logo.name}
                            className="h-4 w-auto max-w-none object-contain opacity-50 transition-opacity duration-300 hover:opacity-90 sm:h-5"
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </section>
            </div>
          </section>
        </div>
      </div>

      {/* ============================================================
          SCROLL-REVEAL INTRO + APP MOCKUP
          Shared white surface so the black panel's shadow can bleed
          continuously into the mockup section instead of being cut.
          ============================================================ */}
      <div className="bg-white">
        <section className="pb-24">
          <div className="rounded-b-[28px] bg-black px-5 py-32 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.55)] md:px-7 md:py-48">
            <div className="mx-auto max-w-4xl">
              <ScrollRevealText
                className="font-sans text-[1.5rem] font-normal leading-[1.35] tracking-[-0.02em] sm:text-[1.875rem] md:text-[2.75rem] md:leading-[1.3] pb-8"
                text={t.scrollReveal}
              />
              <a
                href={APP_TRIAL_URL}
                className="group inline-flex items-center gap-1.5 text-[1.25rem] font-medium text-white/40 transition-colors duration-300 ease-out hover:text-white md:text-[1.4375rem]"
              >
                <span className="pb-1 decoration-current underline-offset-4">{t.trialCta}</span>
                <ArrowRight size={18} strokeWidth={2} className="transition-transform duration-300 ease-out group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </section>

        {/* ============================================================
          APP MOCKUP – sticky stacked panels
          ============================================================ */}
        <div id="app" className="scroll-mt-16">
          <MockupShowcase panels={mockupPanels} />
        </div>
      </div>

      {/* ============================================================
          INVERTER / BATTERY – primary compatibility (live today)
          ============================================================ */}
      <section id="kompabilitet" className="scroll-mt-16 bg-white px-3 pt-3 md:px-4 md:pt-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative order-1 min-h-[18rem] overflow-hidden bg-neutral-900 lg:order-none lg:min-h-[36rem]">
            <video
              className="absolute inset-0 h-full w-full object-cover opacity-80"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden
            >
              <source src="/864_1080x60_shots_so.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
          </div>

          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t.inverter.badge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.inverter.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.inverter.body}
              </p>

              <BrandList brands={inverterBrands} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ELECTRIC CAR – split feature (image left, text right)
          ============================================================ */}
      <section className="bg-white px-3 pt-3 md:px-4 md:pt-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="/326740_1_5.jpg"
              alt={t.car.imgAlt}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>

          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.car.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.car.body}
              </p>

              {/* Supported car brands */}
              <BrandList brands={carBrands} note={t.car.note} locale={locale} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          EV CHARGING – split feature (text panel + image)
          ============================================================ */}
      <section className="bg-white px-3 py-3 md:px-4 md:py-4">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[1.1fr_0.9fr]">
          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.charging.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.charging.body}
              </p>

              {/* Supported EV chargers */}
              <BrandList brands={evChargers} note={t.charging.note} locale={locale} />
            </div>
          </div>

          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="/ev-charger.jpg"
              alt={t.charging.imgAlt}
              className="absolute inset-0 h-full w-full object-cover object-right transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
          HEAT PUMP – split feature (image left, text right)
          ============================================================ */}
      <section className="bg-white px-3 pb-0 md:px-4 md:pb-0">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 overflow-hidden rounded-[8px] lg:grid-cols-[0.9fr_1.1fr]">
          {/* Image */}
          <div className="relative order-1 min-h-[18rem] overflow-hidden lg:order-none lg:min-h-[36rem]">
            <img
              src="pexels-aibek-skakov-418917601-29398678.jpg"
              alt={t.heating.imgAlt}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
            />
          </div>

          {/* Text panel */}
          <div className="relative order-2 flex flex-col justify-center gap-12 bg-neutral-100 p-8 sm:p-12 md:p-14 lg:order-none lg:min-h-[36rem]">
            <div className="max-w-xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-neutral-900/10 bg-white px-3 py-1.5 text-[0.75rem] font-medium tracking-tight text-neutral-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {t.pilotBadge}
              </span>
              <h2 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-[2.125rem] md:text-[2.5rem]">
                {renderAccented(t.heating.heading, darkAccent)}
              </h2>
              <p className="mt-5 max-w-md text-[1rem] leading-relaxed text-neutral-600 md:text-[1.125rem]">
                {t.heating.body}
              </p>

              {/* Supported heat pump / HVAC brands */}
              <BrandList brands={heatPumps} note={t.heating.note} locale={locale} />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          BENTO WORK / FEATURE GRID
          ============================================================ */}
      <section id="funktioner" className="scroll-mt-16 bg-white px-3 py-3 md:px-4 md:py-4 pb-0 md:pb-0">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* --- Card: Solproduktion --- */}
          <VideoBentoCard
            video="/531_1080x60_shots_so.mp4"
            icon={<Sun size={18} strokeWidth={1.75} />}
            title={t.bento.solar.title}
            subtitle={t.bento.solar.subtitle}
            description={t.bento.solar.description}
          />

          {/* --- Card: Batterilager --- */}
          <VideoBentoCard
            video="/864_1080x60_shots_so.mp4"
            icon={<BatteryCharging size={18} strokeWidth={1.75} />}
            title={t.bento.battery.title}
            subtitle={t.bento.battery.subtitle}
            description={t.bento.battery.description}
          />

          {/* --- Card: Intelligent drift --- */}
          <VideoBentoCard
            video="/266_1080x60_shots_so.mp4"
            icon={<TrendingUp size={18} strokeWidth={1.75} />}
            title={t.bento.operation.title}
            subtitle={t.bento.operation.subtitle}
            description={t.bento.operation.description}
          />

          {/* --- Card: Effekttoppar (dark) --- */}
          <div className="group relative flex h-[48svh] flex-col overflow-hidden rounded-[8px] bg-gradient-to-br from-[#262626] to-[#161616] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:h-[62svh] sm:max-h-[610px]">
            {/* Centered intro */}
            <div className="relative z-10 mx-auto max-w-sm pt-2 text-center">
              <h3 className="text-balance font-display text-[1.5rem] font-normal leading-[1.1] tracking-[-0.02em] text-white md:text-[1.875rem]">
                {t.bento.peaks.title}
              </h3>
              <p className="mx-auto mt-2 max-w-xs text-[0.875rem] leading-relaxed text-white/55">
                {t.bento.peaks.body}
              </p>
            </div>

            {/* Energy-day chart fills the remaining space below */}
            <PeakChart className="mt-5 min-h-0 flex-1" />
          </div>

          {/* --- Card: Stödtjänster (dark neutral, globe, wide, badge) --- */}
          <div className="group relative flex h-[48svh] flex-col justify-start overflow-hidden rounded-[8px] bg-gradient-to-br from-[#262626] to-[#161616] p-5 transition-shadow duration-300 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.25)] sm:col-span-2 sm:h-[62svh] sm:max-h-[610px]">
            {/* Globe rising from the bottom */}
            <BrandGlobe sizeFactor={1.6} offsetY="58%" />
            {/* Gradient overlay – darker at the top where the text sits */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-transparent" />

            <div className="relative z-10 mx-auto max-w-md pt-4 text-center">
              <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[0.75rem] font-medium tracking-tight text-white/85 backdrop-blur-sm">
                <Flag locale="sv" className="h-3" />
                {t.bento.sweden.badge}
              </span>
              <h3 className="text-balance font-display text-[1.875rem] font-normal leading-[1.05] tracking-[-0.02em] text-white md:text-[2.25rem]">
                {t.bento.sweden.title}
              </h3>
              <p className="mt-3 text-[0.875rem] leading-relaxed text-white/60 md:text-[1rem]">
                {t.bento.sweden.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TIMELINE
          ============================================================ */}
      <div className="bg-gradient-to-b from-white via-[#f6f8f8] to-frost text-pine">
        <Timeline locale={locale} />
      </div>

      {/* ============================================================
          FAQ
          ============================================================ */}
      <section id="faq" className="scroll-mt-16 bg-white px-5 py-24 md:px-7 md:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[0.875rem] tracking-tight text-black/40">{t.faq.label}</p>
          <h2 className="mx-auto mt-4 max-w-2xl text-balance font-display text-[2rem] font-normal leading-[1.08] tracking-[-0.03em] text-black sm:text-[2.5rem] md:text-[3rem]">
            {renderAccented(t.faq.heading, "font-newsreader font-normal italic text-black/70")}
          </h2>
        </div>
        <FaqAccordion items={faqItems} />
      </section>

      {/* ============================================================
          CTA + FOOTER
          ============================================================ */}
      <footer className="bg-black px-3 pb-8 pt-0 md:px-4 md:pb-10">
        {/* ── CTA ── full-bleed & seamless at all sizes */}
        <section className="relative -mx-3 overflow-hidden bg-gradient-to-b from-[#161616] to-black px-6 py-24 md:-mx-4 md:px-10 md:py-36">
          {/* Soft top glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[min(820px,90%)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.16),transparent)]"
          />

          <div className="relative z-10 mx-auto max-w-3xl text-center">
            <h2 className="mx-auto max-w-3xl text-balance font-display text-[2.25rem] font-medium leading-[1.04] tracking-[-0.035em] text-white sm:text-[2.75rem] md:text-[3.5rem]">
              {renderAccented(t.cta.heading)}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[1.0625rem] leading-relaxed text-white/55">
              {t.cta.body}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3">
              <a
                href={APP_TRIAL_URL}
                onClick={() => capture("footer_trial_cta_clicked")}
                className="group inline-flex w-full items-center justify-center rounded-[6px] bg-[#2563EB] px-6 py-3.5 text-[0.9375rem] font-medium text-white transition-colors hover:bg-[#1D4ED8] sm:w-auto"
              >
                {t.trialCta}
                <span className="grid grid-cols-[0fr] opacity-0 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:ml-2.5 group-hover:grid-cols-[1fr] group-hover:opacity-100">
                  <span className="overflow-hidden">
                    <span className="flex h-6 w-6 items-center justify-center rounded-[3px] border border-white/30 text-white">
                      <ArrowRight size={13} strokeWidth={2} />
                    </span>
                  </span>
                </span>
              </a>
              <a
                href={APP_LOGIN_URL}
                onClick={() => capture("footer_login_clicked")}
                className="inline-flex items-center justify-center px-6 py-2 text-[0.9375rem] font-medium text-white/65 transition-colors hover:text-white"
              >
                {t.cta.login}
              </a>
            </div>
          </div>
        </section>

        {/* ── Footer body ── */}
        <div className="mx-auto max-w-7xl px-2 md:px-3">
          <div className="mt-20 flex flex-col gap-12 md:mt-24 md:flex-row md:items-start md:justify-between md:gap-8">
            <div className="max-w-md">
              <p className="text-balance font-sans text-[1.75rem] font-normal leading-[1.12] tracking-[-0.03em] text-white sm:text-[2.125rem]">
                {renderAccented(
                  t.footer.tagline,
                  "whitespace-nowrap font-newsreader font-normal italic text-white/55",
                )}
              </p>
              <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[0.8125rem] text-white/55">
                <Flag locale="sv" className="h-3.5" />
                {t.footer.developedInSweden}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-10 sm:gap-16">
              <div className="flex flex-col gap-3.5">
                <span className="text-[0.75rem] uppercase tracking-wider text-white/35">{t.footer.product}</span>
                <a href="#app" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.app}
                </a>
                <a href="#kompabilitet" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.compatibility}
                </a>
                <a href="#funktioner" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.features}
                </a>
                <a href="#faq" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.nav.faq}
                </a>
                <a href={localePath(locale, "/foretag")} className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.footer.forInstallers}
                </a>
              </div>
              <div className="flex flex-col gap-3.5">
                <span className="text-[0.75rem] uppercase tracking-wider text-white/35">{t.footer.aiControl}</span>
                {/* Swedish keyword pages – only surfaced on the Swedish site */}
                {locale === "sv" && (
                  <>
                    <a href="/ai-styrning-solinteg" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                      Solinteg
                    </a>
                    <a href="/ai-styrning-sunwoda" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                      Sunwoda
                    </a>
                    <a href="/ai-styrning-vaxelriktare" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                      Växelriktare
                    </a>
                    <a href="/ai-styrning-batteri" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                      Batteri
                    </a>
                    <a href="/intelligent-energistyrning" className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                      Intelligent energistyrning
                    </a>
                  </>
                )}
                <a href={localePath(locale, "/company")} className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.footer.about}
                </a>
                <a href={localePath(locale, "/support")} className="text-[0.875rem] text-white/65 transition-colors hover:text-white">
                  {t.footer.support}
                </a>
              </div>
            </div>
          </div>

          {/* Bottom legal bar */}
          <div className="mt-16 flex flex-col gap-4 border-t border-white/10 pt-8 text-[0.75rem] text-white/35 sm:flex-row sm:items-center sm:justify-between">
            <span>{t.footer.rights}</span>
            <div className="flex gap-6">
              {/* Legal documents exist in Swedish only */}
              <a href="/integritetspolicy" className="transition-colors hover:text-white/70">
                {t.footer.privacy}
              </a>
              <a href="/anvandarvillkor" className="transition-colors hover:text-white/70">
                {t.footer.terms}
              </a>
              <CookieSettingsLink locale={locale} />
            </div>
          </div>

          {/* Oversized wordmark flourish – edges fade so it reads as designed */}
          <img
            src="/brand/intelligence-l.svg"
            alt=""
            aria-hidden
            className="pointer-events-none mt-12 w-full select-none opacity-[0.45] invert [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)] md:mt-16"
          />
        </div>
      </footer>
    </main>
  );
}
